import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, Copy, ExternalLink, Loader2, Radio, Video, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/Components/ui/button';
import { PlanLockCard, InsufficientCreditsCard, CreditCostHint } from '../../Components/PlanFeatureLock';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import { Text } from '@/Components/ui/typography';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/dialog';
import { cn } from '@/lib/utils';
import { getApiUrl } from '../../../config/api';
import { createManualSchedule, getActorEmail, openLiveMeetRoomFromBooking } from './liveMeetApi';
import { startInstantOpenLinkLiveMeet } from './instantOpenLinkLiveMeet';
import InterviewerPanelPicker from './InterviewerPanelPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import {
  createClientRequestId,
  DEFAULT_MANUAL_MEET_DURATION,
  DEFAULT_MEETING_TIMEZONE,
  getManualMeetDurationLabel,
  getMinTimeForDate,
  getTodayDateString,
  isValidIanaTimezone,
  MANUAL_MEET_DURATION_OPTIONS,
  MEETING_TIMEZONE_OPTIONS,
  resolveManualMeetEndTime,
  toIsoInTimezone,
  validateManualScheduleWindow,
} from './meetingScheduleUtils';

/** Set true when automated slot-picker flow should be available in the UI. */
const AUTOMATED_SCHEDULING_ENABLED = false;
const LIVE_MEET_LABEL = 'Search Job Meet';
const JOIN_LIVE_MEET_LABEL = 'Join Search Job Meet';
const INSTANT_LIVE_MEET_LABEL = 'Instant Search Job Meet';
const DEFAULT_INTERVIEW_ROUND = 'technical_round_1';
const INTERVIEW_ROUND_OPTIONS = [
  { value: 'technical_round_1', label: 'Technical Round 1' },
  { value: 'technical_round_2', label: 'Technical Round 2' },
  { value: 'technical_round_3', label: 'Technical Round 3' },
  { value: 'technical_round_4', label: 'Technical Round 4' },
  { value: 'technical_round_5', label: 'Technical Round 5' },
  { value: 'managerial_round_1', label: 'Managerial Round 1' },
  { value: 'managerial_round_2', label: 'Managerial Round 2' },
  { value: 'managerial_round_3', label: 'Managerial Round 3' },
  { value: 'hr_round_1', label: 'HR Round 1' },
  { value: 'hr_round_2', label: 'HR Round 2' },
  { value: 'hr_round_3', label: 'HR Round 3' },
  { value: 'screening_round', label: 'Screening Round' },
  { value: 'assignment_round', label: 'Assignment Round' },
  { value: 'coding_round', label: 'Coding Round' },
  { value: 'system_design_round', label: 'System Design Round' },
  { value: 'culture_fit_round', label: 'Culture Fit Round' },
  { value: 'final_round', label: 'Final Round' },
  { value: 'offer_discussion', label: 'Offer Discussion' },
];

const allSchedulingOptions = [
  {
    id: 'automated',
    title: 'Automated Scheduling',
    description: 'Let the system fetch available slots and let the candidate choose a time.',
    icon: Radio,
    disabled: !AUTOMATED_SCHEDULING_ENABLED,
    disabledHint: 'Temporarily unavailable',
  },
  {
    id: 'manual',
    title: 'Manual Scheduling',
    description: 'Select date and time manually and create the interview directly.',
    icon: CalendarClock,
  },
  {
    id: 'instant',
    title: INSTANT_LIVE_MEET_LABEL,
    description: `Start a ${LIVE_MEET_LABEL} room immediately.`,
    icon: Zap,
    // Temporarily disabled — instant meeting is broken. Shown greyed-out so the
    // option is visibly "off for now" rather than silently removed. Re-enable by
    // deleting these two lines.
    unavailable: true,
    disabledHint: 'Instant meetings are temporarily unavailable — please use Manual Scheduling.',
  },
];

const schedulingOptions = allSchedulingOptions.filter((option) => !option.disabled);

const liveMeetSchedulingOptions = schedulingOptions.filter((option) =>
  ['manual', 'instant'].includes(option.id)
);

const buildDefaultMeetingTitle = (jobPosition, candidate) => {
  const position = String(jobPosition || '').trim();
  const name = String(candidate || '').trim();
  if (position && name) return `Interview for ${position} — ${name}`;
  if (position) return `Interview for ${position}`;
  return name ? `Interview for ${name}` : 'Interview';
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FieldError = ({ message }) =>
  message ? (
    <div className="mt-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  ) : null;

const validateInterviewerPanel = (panel, candidateEmail) => {
  const candidateNormalized = String(candidateEmail || '').trim().toLowerCase();
  if (!panel?.length || !String(panel[0]?.email || '').trim()) {
    return 'At least one interviewer is required. Add a host above.';
  }
  const invalidPanelEntry = (panel || []).find((entry) => {
    const email = String(entry?.email || '').trim().toLowerCase();
    return !EMAIL_REGEX.test(email) || email === candidateNormalized;
  });
  if (!invalidPanelEntry) return '';
  const bad = invalidPanelEntry.email || '';
  if (bad && bad.toLowerCase() === candidateNormalized) {
    return 'A panel interviewer cannot be the candidate. Remove the candidate email from the panel.';
  }
  return `${bad || 'A panel entry'} is not a valid interviewer email.`;
};

const providerOptions = [
  {
    id: 'google_meet',
    title: 'Google Meet',
    description: 'Uses Google Calendar and Google Meet.',
  },
  {
    id: 'live_meet',
    title: LIVE_MEET_LABEL,
    description: `Uses your internal ${LIVE_MEET_LABEL} room.`,
  },
];

const resolveInterviewRoundLabel = (value) =>
  INTERVIEW_ROUND_OPTIONS.find((option) => option.value === value)?.label ||
  INTERVIEW_ROUND_OPTIONS[0].label;

const MeetingCreateDialog = ({
  open,
  onClose,
  embedded = false,
  candidate,
  jobDetails,
  onBookingCreated,
  onOpenAutomatedFlow,
}) => {
  const candidateEmail = candidate?.email || candidate?.candidate_email || '';
  const candidateName = candidate?.name || candidate?.full_name || candidateEmail || 'Candidate';
  const jobId = String(candidate?.job_id || jobDetails?.job_id || '').trim();
  const jobTitle = jobDetails?.job_title || jobDetails?.title || 'Live Interview';

  const [schedulingType, setSchedulingType] = useState('manual');
  const [meetingProvider, setMeetingProvider] = useState('live_meet');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [instantBooking, setInstantBooking] = useState(null);
  const submitLockRef = useRef(false);

  // Plan entitlement for Live Meet (Search Job Meet). Lock the schedule action
  // when the company's plan doesn't include live_meet, and show the fixed
  // per-interview credit cost. Resolved by job (backend maps it to the owning
  // company), falling back to the stored company id.
  const [ent, setEnt] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let query = '';
    if (jobId) {
      query = `job_id=${encodeURIComponent(jobId)}`;
    } else {
      let cid = '';
      try { cid = localStorage.getItem('companyAccountId') || ''; } catch (_) { cid = ''; }
      if (cid) query = `company_id=${encodeURIComponent(cid)}`;
    }
    if (!query) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/entitlements/me?${query}`), { credentials: 'include' });
        const data = res.ok ? await res.json() : null;
        if (!cancelled) setEnt(data || null);
      } catch (_) {
        if (!cancelled) setEnt(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, jobId]);

  const liveMeetEnforced = !!ent?.enforced;
  const liveMeetLocked = liveMeetEnforced && ent?.features?.live_meet === false;
  const liveMeetCost = Number(ent?.test_costs?.LIVE_MEET ?? 1);
  const liveMeetBalance = Number(ent?.balance ?? 0);
  const liveMeetCantAfford = liveMeetEnforced && liveMeetBalance < liveMeetCost;

  // Default the interviewer email to BLANK so HR explicitly picks the
  // external interviewer's address. Pre-filling HR's own email caused
  // confusion in demos — HR would unintentionally send the meeting to
  // themselves and the candidate would believe HR is the interviewer.
  //
  // The interviewer field supports multiple panel interviewers. Each can
  // optionally be marked Co-host. The logged-in SearchJob.ai user is the
  // scheduling host (manually_created_by). Each panel member receives their
  // own tokenized join link by email.
  const [interviewerPanel, setInterviewerPanel] = useState([]);
  const primaryInterviewer = interviewerPanel[0] || null;
  const interviewerEmail = primaryInterviewer?.email || '';
  const interviewerName = primaryInterviewer?.name || '';
  const defaultMeetingTitle = useMemo(
    () => buildDefaultMeetingTitle(jobTitle, candidateName),
    [jobTitle, candidateName]
  );
  const [meetingTitle, setMeetingTitle] = useState(defaultMeetingTitle);
  // Prefill the company name (editable). The JD's company_name is often empty,
  // so fall back to the logged-in employer's company name (stored in
  // localStorage by useAuth) — that's the scheduling host's company.
  const resolveCompanyName = () => {
    let ls = '';
    try { ls = localStorage.getItem('company_name') || ''; } catch (_) { ls = ''; }
    if (ls === 'Company') ls = '';   // useAuth's placeholder default, not a real name
    return jobDetails?.company_name || jobDetails?.companyName || ls || '';
  };
  const [companyName, setCompanyName] = useState(resolveCompanyName);
  // Prefill the company name — never overwriting what the user has typed:
  //   1) JD / localStorage (synchronous), else
  //   2) fetch the logged-in employer's company profile (companyAccountId is
  //      reliably present even when company_name isn't cached).
  useEffect(() => {
    const val = resolveCompanyName();
    if (val) {
      setCompanyName((prev) => (prev && prev.trim() ? prev : val));
      return;
    }
    let companyId = '';
    try { companyId = localStorage.getItem('companyAccountId') || ''; } catch (_) { companyId = ''; }
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/hirewithus/company/details/${companyId}`), {
          credentials: 'include',
        });
        if (cancelled || !res.ok) return;
        const data = await res.json();
        const name = String(data?.companyName || data?.company_name || '').trim();
        if (name && name !== 'Company') {
          setCompanyName((prev) => (prev && prev.trim() ? prev : name));
        }
      } catch (_) { /* prefill is best-effort */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDetails?.company_name, jobDetails?.companyName]);
  const [interviewRound, setInterviewRound] = useState(DEFAULT_INTERVIEW_ROUND);
  const [meetingDate, setMeetingDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [meetingDuration, setMeetingDuration] = useState(DEFAULT_MANUAL_MEET_DURATION);
  const [timezone, setTimezone] = useState(DEFAULT_MEETING_TIMEZONE);
  const [manualNote, setManualNote] = useState('Scheduled manually from Meeting modal');

  const showProviderSection = schedulingType === 'automated';
  const isManualForm = schedulingType === 'manual';
  const effectiveMeetingProvider = isManualForm ? 'live_meet' : meetingProvider;
  const minDate = useMemo(() => getTodayDateString(), []);
  const minTime = useMemo(() => getMinTimeForDate(meetingDate), [meetingDate]);

  useEffect(() => {
    setMeetingTitle(defaultMeetingTitle);
  }, [defaultMeetingTitle]);

  const endTime = useMemo(
    () => resolveManualMeetEndTime({ startTime, durationValue: meetingDuration }),
    [meetingDuration, startTime]
  );
  const durationLabel = useMemo(
    () => getManualMeetDurationLabel(meetingDuration),
    [meetingDuration]
  );
  const startIso = useMemo(
    () => toIsoInTimezone(meetingDate, startTime, timezone),
    [meetingDate, startTime, timezone]
  );
  const endIso = useMemo(
    () => toIsoInTimezone(meetingDate, endTime, timezone),
    [meetingDate, endTime, timezone]
  );

  const manualScheduleWindowError = useMemo(() => {
    if (!meetingDate || !startTime || !meetingDuration || !endTime) return '';
    if (!isValidIanaTimezone(timezone)) return '';
    if (!startIso || !endIso) return '';
    return validateManualScheduleWindow({ startIso, endIso }) || '';
  }, [endIso, endTime, meetingDate, meetingDuration, startIso, startTime, timezone]);

  const manualFormFieldErrors = useMemo(() => {
    const errors = {};
    if (!companyName.trim()) {
      errors.companyName = 'Company name is required.';
    }
    const panelError = validateInterviewerPanel(interviewerPanel, candidateEmail);
    if (panelError) {
      errors.interviewerPanel = panelError;
    }
    if (!meetingDate) {
      errors.meetingDate = 'Date is required.';
    }
    if (!startTime) {
      errors.startTime = 'Start time is required.';
    }
    if (!meetingDuration) {
      errors.meetingDuration = 'Duration is required.';
    }
    if (!timezone.trim()) {
      errors.timezone = 'Timezone is required.';
    } else if (!isValidIanaTimezone(timezone)) {
      errors.timezone = 'Enter a valid timezone (for example Asia/Kolkata).';
    } else if (manualScheduleWindowError) {
      errors.scheduleWindow = manualScheduleWindowError;
    }
    return errors;
  }, [
    candidateEmail,
    companyName,
    interviewerPanel,
    manualScheduleWindowError,
    meetingDate,
    meetingDuration,
    startTime,
    timezone,
  ]);

  const instantFormFieldErrors = useMemo(() => {
    const errors = {};
    if (!companyName.trim()) {
      errors.companyName = 'Company name is required.';
    }
    const panelError = validateInterviewerPanel(interviewerPanel, candidateEmail);
    if (panelError) {
      errors.interviewerPanel = panelError.replace(
        'At least one interviewer is required',
        'At least one participant is required'
      );
    }
    return errors;
  }, [candidateEmail, companyName, interviewerPanel]);

  const canSubmitManual = useMemo(() => {
    if (!candidateEmail || !jobId) return false;
    return Object.keys(manualFormFieldErrors).length === 0;
  }, [candidateEmail, jobId, manualFormFieldErrors]);

  const canSubmitInstant = useMemo(() => {
    if (!candidateEmail || !jobId) return false;
    return Object.keys(instantFormFieldErrors).length === 0;
  }, [candidateEmail, instantFormFieldErrors, jobId]);

  const canSubmitPrimary = useMemo(() => {
    // A Live Meet (Search Job Meet) booking is blocked when the plan excludes
    // it or the balance is short. Manual & instant are always Live Meet;
    // automated is only Live Meet when that provider is selected.
    const wantsLiveMeet =
      schedulingType === 'instant' ||
      schedulingType === 'manual' ||
      (schedulingType === 'automated' && meetingProvider === 'live_meet');
    if (wantsLiveMeet && (liveMeetLocked || liveMeetCantAfford)) return false;
    if (schedulingType === 'manual') return canSubmitManual;
    if (schedulingType === 'instant') return canSubmitInstant;
    if (schedulingType === 'automated') {
      if (!candidateEmail || !jobId || !companyName.trim()) return false;
      if (meetingProvider === 'google_meet' && !interviewerEmail) return false;
      return true;
    }
    return false;
  }, [
    canSubmitInstant,
    canSubmitManual,
    candidateEmail,
    companyName,
    interviewerEmail,
    jobId,
    meetingProvider,
    schedulingType,
    liveMeetLocked,
    liveMeetCantAfford,
  ]);

  const markFormTouched = () => {
    setShowValidation(true);
  };

  const resetAndClose = () => {
    setError('');
    setShowValidation(false);
    setIsSubmitting(false);
    setInstantBooking(null);
    submitLockRef.current = false;
    onClose();
  };

  const copyInstantInviteLink = async () => {
    const link =
      instantBooking?.candidate_invite_url ||
      instantBooking?.meet_link ||
      '';
    if (!link) {
      toast.error('No invite link available yet.');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Candidate invite link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const basePayload = () => {
    const normalizedInterviewerEmail = String(interviewerEmail || '').trim().toLowerCase();
    // Primary stays in the top-level interviewer_email/interviewer_name fields
    // (backend backwards compat). Additional panelists go into
    // additional_interviewers. Optional is_cohost is per person and defaults false.
    const additionalInterviewers = (interviewerPanel || [])
      .slice(1)
      .map((entry) => ({
        email: String(entry?.email || '').trim().toLowerCase(),
        name: (entry?.name || '').trim(),
        is_cohost: Boolean(entry?.is_cohost),
      }))
      .filter((entry) => entry.email);
    return {
      candidate_email: candidateEmail,
      candidate_name: candidateName,
      interviewer_email: normalizedInterviewerEmail,
      interviewer_name: (interviewerName || '').trim() || normalizedInterviewerEmail,
      interviewer_is_cohost: Boolean(primaryInterviewer?.is_cohost),
      additional_interviewers: additionalInterviewers,
      job_id: jobId,
      job_title: jobTitle,
      meeting_title: meetingTitle.trim() || defaultMeetingTitle,
      company_name: companyName.trim(),
      interview_round: interviewRound,
      timezone,
      meeting_provider: effectiveMeetingProvider,
      scheduling_mode: schedulingType,
      manual_note: manualNote,
      transcript_enabled: true,
    };
  };

  const handlePrimaryAction = async () => {
    if (submitLockRef.current || isSubmitting) return;

    setError('');
    if (!candidateEmail) {
      setError('Candidate email is required.');
      return;
    }
    if (!jobId) {
      setError('Job id is missing for this candidate.');
      return;
    }

    if (schedulingType === 'automated') {
      markFormTouched();
      if (!companyName.trim() || (!interviewerEmail && meetingProvider === 'google_meet')) {
        return;
      }
      resetAndClose();
      onOpenAutomatedFlow?.({ meetingProvider });
      return;
    }

    if (schedulingType === 'manual' && Object.keys(manualFormFieldErrors).length) {
      markFormTouched();
      return;
    }

    if (schedulingType === 'instant' && Object.keys(instantFormFieldErrors).length) {
      markFormTouched();
      return;
    }

    const normalizedInterviewerEmail = String(interviewerEmail || '').trim().toLowerCase();

    if (schedulingType === 'instant') {
      submitLockRef.current = true;
      setIsSubmitting(true);
      setInstantBooking(null);
      try {
        const { booking } = await startInstantOpenLinkLiveMeet({
          jobId,
          jobTitle,
          candidateEmail,
          candidateName,
          interviewerEmail: normalizedInterviewerEmail,
          interviewerName: interviewerName || normalizedInterviewerEmail,
          additionalInterviewers: (interviewerPanel || []).slice(1).map((entry) => ({
            email: entry?.email || '',
            name: entry?.name || '',
            is_cohost: Boolean(entry?.is_cohost),
          })),
          interviewerIsCohost: Boolean(primaryInterviewer?.is_cohost),
          companyName: companyName.trim(),
          interviewRound,
          clientRequestId: createClientRequestId(),
        });
        toast.success(`${INSTANT_LIVE_MEET_LABEL} started`);
        onBookingCreated?.(booking);
        setInstantBooking(booking);
        if (!openLiveMeetRoomFromBooking(booking)) {
          setError(`${LIVE_MEET_LABEL} started. Use the ${JOIN_LIVE_MEET_LABEL} button below if the browser blocked the new tab.`);
        }
      } catch (err) {
        submitLockRef.current = false;
        const message = err.message || `Failed to start ${INSTANT_LIVE_MEET_LABEL}`;
        setError(message);
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);

    try {
      const data = await createManualSchedule({
        ...basePayload(),
        start_time: startIso,
        end_time: endIso,
        client_request_id: createClientRequestId(),
      });
      const booking = data?.booking;
      const emailStatus = booking?.email_status;
      if (emailStatus === 'failed') {
        const errMsg =
          booking?.email_errors?.candidate ||
          booking?.email_errors?.interviewer ||
          'Meeting was saved but confirmation emails could not be sent. Check MEETING_GMAIL_* in backend .env.';
        toast.warning(errMsg);
      } else if (emailStatus === 'partial') {
        toast.warning('Meeting scheduled. One confirmation email may have failed.');
      } else if (emailStatus === 'sending') {
        toast.success(`${LIVE_MEET_LABEL} scheduled. Confirmation emails are being sent.`);
      } else {
        toast.success(
          effectiveMeetingProvider === 'google_meet' ? 'Google Meet scheduled' : `${LIVE_MEET_LABEL} scheduled`
        );
      }
      onBookingCreated?.(booking);
      // Manual Live Meet: stay on Applications — host joins later from Activity, not lobby preview.
      if (embedded) {
        submitLockRef.current = false;
        setIsSubmitting(false);
        return;
      }
      resetAndClose();
    } catch (err) {
      submitLockRef.current = false;
      const message =
        err.message ||
        err.response?.data?.error ||
        (err.code === 'ECONNABORTED'
          ? 'Request timed out. Check backend is running on port 5000.'
          : null) ||
        'Failed to schedule meeting';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const schedulerEmail = useMemo(() => getActorEmail(), []);
  const cohostCount = interviewerPanel.filter((entry) => entry?.is_cohost).length;
  const interviewerSummaryValue =
    interviewerPanel.length === 0
      ? '—'
      : interviewerPanel.length === 1
        ? `${interviewerName || interviewerEmail || '—'}${primaryInterviewer?.is_cohost ? ' (Co-host)' : ''}`
        : `${interviewerName || interviewerEmail} + ${interviewerPanel.length - 1} interviewer${interviewerPanel.length > 2 ? 's' : ''}${cohostCount ? ` · ${cohostCount} Co-host${cohostCount > 1 ? 's' : ''}` : ''}`;

  const summaryRows = [
    ...(!isManualForm
      ? [
          {
            label: 'Scheduling',
            value: schedulingOptions.find((o) => o.id === schedulingType)?.title,
          },
        ]
      : []),
    ...(showProviderSection
      ? [
          {
            label: 'Provider',
            value: providerOptions.find((o) => o.id === meetingProvider)?.title,
          },
        ]
      : []),
    {
      label: 'Date & Time',
      value:
        meetingDate && startTime && endTime
          ? `${meetingDate} ${startTime} – ${endTime}${durationLabel ? ` (${durationLabel})` : ''}`
          : '—',
    },
    { label: 'Candidate', value: `${candidateName} (${candidateEmail || 'no email'})` },
    { label: 'Company', value: companyName.trim() || '—' },
    { label: 'Round', value: resolveInterviewRoundLabel(interviewRound) },
    { label: 'Host', value: schedulerEmail || '—' },
    {
      label: interviewerPanel.length > 1 ? `Interviewers (${interviewerPanel.length})` : 'Interviewer',
      value: interviewerSummaryValue,
    },
    { label: 'Timezone', value: timezone },
  ];

  const primaryLabel = (() => {
    if (schedulingType === 'automated') return 'Continue';
    const isLiveMeetAction =
      schedulingType === 'instant' ||
      schedulingType === 'manual' ||
      effectiveMeetingProvider === 'live_meet';
    if (isLiveMeetAction && liveMeetLocked) return 'Not in your plan';
    const costSuffix = isLiveMeetAction && liveMeetEnforced ? ` · ${liveMeetCost} cr` : '';
    if (schedulingType === 'instant') return `Start ${LIVE_MEET_LABEL}${costSuffix}`;
    if (effectiveMeetingProvider === 'google_meet') return 'Schedule Google Meet';
    return `Schedule ${LIVE_MEET_LABEL}${costSuffix}`;
  })();

  const handleSchedulingTypeChange = (optionId) => {
    setSchedulingType(optionId);
    setInstantBooking(null);
    setError('');
    setShowValidation(false);
    if (optionId === 'instant' || optionId === 'manual') {
      setMeetingProvider('live_meet');
    }
  };

  const activeFieldErrors = showValidation
    ? schedulingType === 'manual'
      ? manualFormFieldErrors
      : schedulingType === 'instant'
        ? instantFormFieldErrors
        : {}
    : {};

  const renderSchedulingTypeSelector = () => {
    const options = embedded ? liveMeetSchedulingOptions : schedulingOptions;

    if (embedded) {
      return (
        <div>
          <Text className="font-semibold text-slate-900 mb-3">Scheduling type</Text>
          <div className="flex w-full overflow-hidden rounded-lg border border-slate-200 bg-white">
            {options.map((option, index) => {
              const selected = schedulingType === option.id;
              const isDisabled = Boolean(option.disabled || option.unavailable);
              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={isDisabled}
                  title={isDisabled ? (option.disabledHint || 'Unavailable') : undefined}
                  onClick={() => {
                    if (isDisabled) return;
                    handleSchedulingTypeChange(option.id);
                  }}
                  className={cn(
                    'flex-1 px-3 py-3 text-center text-sm font-medium transition-colors sm:px-4',
                    index > 0 && 'border-l border-slate-200',
                    isDisabled
                      ? 'cursor-not-allowed bg-slate-50 text-slate-400'
                      : selected
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-600 hover:bg-slate-50'
                  )}
                >
                  {option.title}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div>
        <Text className="font-semibold text-slate-900 mb-3">Scheduling Type</Text>
        <div
          className={cn(
            'grid gap-3',
            options.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
          )}
        >
          {options.map((option) => {
            const Icon = option.icon;
            const selected = schedulingType === option.id;
            const isDisabled = Boolean(option.disabled || option.unavailable);
            return (
              <button
                key={option.id}
                type="button"
                disabled={isDisabled}
                title={isDisabled ? option.disabledHint || 'Unavailable' : undefined}
                onClick={() => {
                  if (isDisabled) return;
                  handleSchedulingTypeChange(option.id);
                }}
                className={`rounded-xl border p-4 text-left transition ${
                  isDisabled
                    ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'
                    : selected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Icon
                  className={`w-5 h-5 mb-2 ${
                    isDisabled ? 'text-slate-400' : selected ? 'text-primary' : 'text-slate-600'
                  }`}
                />
                <Text className={`font-semibold ${isDisabled ? 'text-slate-500' : 'text-slate-900'}`}>
                  {option.title}
                </Text>
                <Text size="sm" variant="muted" className="mt-1">
                  {isDisabled ? option.disabledHint || option.description : option.description}
                </Text>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (!embedded && !open) {
    return null;
  }

  const panel = (
    <>
        {!embedded ? (
          <DialogHeader>
            <DialogTitle>Interview Schedule</DialogTitle>
            <DialogDescription>
              Choose how you want to schedule this live human interview.
            </DialogDescription>
          </DialogHeader>
        ) : null}

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {liveMeetLocked ? (
          <PlanLockCard
            featureKey="live_meet"
            featureLabel={`${LIVE_MEET_LABEL} interviews`}
            planName={ent?.plan_name}
            cost={liveMeetEnforced ? liveMeetCost : null}
          />
        ) : liveMeetCantAfford ? (
          <InsufficientCreditsCard
            featureLabel={`A ${LIVE_MEET_LABEL} interview`}
            cost={liveMeetCost}
            balance={liveMeetBalance}
          />
        ) : liveMeetEnforced ? (
          <CreditCostHint label={`${LIVE_MEET_LABEL} interview`} cost={liveMeetCost} />
        ) : null}

        <div className="space-y-6">
          {renderSchedulingTypeSelector()}

          {showProviderSection ? (
            <div>
              <Text className="font-semibold text-slate-900 mb-3">Meeting Provider</Text>
              <div className="grid gap-3 sm:grid-cols-2">
                {providerOptions.map((option) => {
                  const selected = meetingProvider === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setMeetingProvider(option.id)}
                      className={`rounded-xl border p-4 text-left transition ${
                        selected
                          ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Text className="font-semibold text-slate-900">{option.title}</Text>
                      <Text size="sm" variant="muted" className="mt-1">
                        {option.description}
                      </Text>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {isManualForm ? (
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <Label>Candidate</Label>
                  <Input value={`${candidateName} (${candidateEmail})`} readOnly />
                </div>
                <div>
                  <InterviewerPanelPicker
                    value={interviewerPanel}
                    onChange={(next) => {
                      markFormTouched();
                      setInterviewerPanel(next);
                    }}
                    candidateEmail={candidateEmail}
                    disabled={isSubmitting}
                    label={
                      <>
                        Interviewer panel <span className="text-red-500">*</span>
                      </>
                    }
                    helperText="Pick interviewers from saved interviewers or type a one-off email. Each gets a personal join link. Optionally mark someone as Co-host. You (logged in) are the scheduling host."
                  />
                  <FieldError message={activeFieldErrors.interviewerPanel} />
                </div>
                <div>
                  <Label>Meeting title</Label>
                  <Input value={meetingTitle} onChange={(e) => setMeetingTitle(e.target.value)} />
                </div>
                <div>
                  <Label>
                    Company name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={companyName}
                    onChange={(e) => {
                      markFormTouched();
                      setCompanyName(e.target.value);
                    }}
                    placeholder="Enter your company name"
                    required
                    aria-invalid={Boolean(activeFieldErrors.companyName)}
                  />
                  <FieldError message={activeFieldErrors.companyName} />
                </div>
                <div>
                  <Label>Interview round</Label>
                  <Select value={interviewRound} onValueChange={setInterviewRound}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select interview round" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERVIEW_ROUND_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>
                    Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="date"
                    min={minDate}
                    value={meetingDate}
                    onChange={(e) => {
                      markFormTouched();
                      setMeetingDate(e.target.value);
                    }}
                    aria-invalid={Boolean(activeFieldErrors.meetingDate)}
                  />
                  <FieldError message={activeFieldErrors.meetingDate} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>
                      Start time <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="time"
                      min={minTime || undefined}
                      value={startTime}
                      onChange={(e) => {
                        markFormTouched();
                        setStartTime(e.target.value);
                      }}
                      aria-invalid={Boolean(activeFieldErrors.startTime)}
                    />
                    <FieldError message={activeFieldErrors.startTime} />
                  </div>
                  <div>
                    <Label>
                      End time <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={meetingDuration}
                      onValueChange={(value) => {
                        markFormTouched();
                        setMeetingDuration(value);
                      }}
                    >
                      <SelectTrigger aria-invalid={Boolean(activeFieldErrors.meetingDuration)}>
                        <SelectValue placeholder="Select end time" />
                      </SelectTrigger>
                      <SelectContent>
                        {MANUAL_MEET_DURATION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {startTime && endTime ? (
                      <Text size="xs" variant="muted" className="mt-1.5">
                        Ends at {endTime}
                      </Text>
                    ) : null}
                    <FieldError message={activeFieldErrors.meetingDuration} />
                  </div>
                </div>
                <FieldError message={activeFieldErrors.scheduleWindow} />
                <div>
                  <Label>
                    Timezone <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={timezone}
                    onValueChange={(value) => {
                      markFormTouched();
                      setTimezone(value);
                    }}
                  >
                    <SelectTrigger aria-invalid={Boolean(activeFieldErrors.timezone)}>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEETING_TIMEZONE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError message={activeFieldErrors.timezone} />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea value={manualNote} onChange={(e) => setManualNote(e.target.value)} rows={2} />
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <Text className="font-semibold text-slate-900">Summary</Text>
                {summaryRows.map((row) => (
                  <div key={row.label}>
                    <Text size="xs" className="uppercase tracking-wide text-slate-500">
                      {row.label}
                    </Text>
                    <Text size="sm" className="text-slate-800">
                      {row.value}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          ) : schedulingType === 'instant' ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
              {instantBooking ? (
                <>
                  <Text size="sm" className="font-semibold text-emerald-900">
                    {LIVE_MEET_LABEL} room is ready
                  </Text>
                  <Text size="sm" className="text-emerald-800">
                    Anyone with the candidate invite link can join. No calendar or scheduled time required.
                  </Text>
                  {(instantBooking.candidate_invite_url || instantBooking.meet_link) && (
                    <Text
                      size="xs"
                      className="break-all text-emerald-900 bg-white/70 rounded-lg px-3 py-2 border border-emerald-200"
                    >
                      {instantBooking.candidate_invite_url || instantBooking.meet_link}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <Label>
                      Company name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={companyName}
                      onChange={(e) => {
                        markFormTouched();
                        setCompanyName(e.target.value);
                      }}
                      placeholder="Enter your company name"
                      required
                      aria-invalid={Boolean(activeFieldErrors.companyName)}
                    />
                    <FieldError message={activeFieldErrors.companyName} />
                  </div>
                  <div>
                    <Label>Interview round</Label>
                    <Select value={interviewRound} onValueChange={setInterviewRound}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select interview round" />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERVIEW_ROUND_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <InterviewerPanelPicker
                      value={interviewerPanel}
                      onChange={(next) => {
                        markFormTouched();
                        setInterviewerPanel(next);
                      }}
                      candidateEmail={candidateEmail}
                      disabled={isSubmitting}
                      variant="instant"
                      label={
                        <>
                          Interviewers <span className="text-red-500">*</span>
                        </>
                      }
                      helperText="Add interviewers who will join via email link. Optionally mark someone as Co-host. You (logged in) are the scheduling host."
                    />
                    <FieldError message={activeFieldErrors.interviewerPanel} />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <Text size="sm" className="text-slate-700">
                Continue to interviewer setup and slot selection. The candidate will choose a time from
                shared slots.
              </Text>
            </div>
          )}
        </div>

        {/* Footer:
            - When standalone (not embedded), keep the classic two-button
              Dialog footer (Cancel + Primary).
            - When embedded inside HumanInterviewHub, the Hub header already
              provides Close, so we render a clean sticky action bar with just
              the primary action (or the Done/Copy/Join trio for the instant
              flow) — eliminating the redundant Cancel button. */}
        {embedded ? (
          <div className="sticky bottom-0 -mx-6 mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-white/95 px-6 py-3 backdrop-blur">
            {instantBooking ? (
              <>
                <Button type="button" variant="outline" onClick={copyInstantInviteLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy link
                </Button>
                <Button
                  type="button"
                  onClick={() => openLiveMeetRoomFromBooking(instantBooking)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {JOIN_LIVE_MEET_LABEL}
                </Button>
              </>
            ) : (
              <Button
                onClick={handlePrimaryAction}
                disabled={isSubmitting || !canSubmitPrimary}
                className="min-w-[180px]"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {schedulingType === 'instant' ? <Video className="w-4 h-4 mr-2" /> : null}
                {primaryLabel}
              </Button>
            )}
          </div>
        ) : (
          <DialogFooter className="gap-2 sm:flex-wrap sm:justify-between">
            <Button variant="outline" onClick={resetAndClose} disabled={isSubmitting}>
              {instantBooking ? 'Done' : 'Cancel'}
            </Button>
            {instantBooking ? (
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={copyInstantInviteLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  type="button"
                  onClick={() => openLiveMeetRoomFromBooking(instantBooking)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {JOIN_LIVE_MEET_LABEL}
                </Button>
              </div>
            ) : (
              <Button onClick={handlePrimaryAction} disabled={isSubmitting || !canSubmitPrimary}>
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {schedulingType === 'instant' ? <Video className="w-4 h-4 mr-2" /> : null}
                {primaryLabel}
              </Button>
            )}
          </DialogFooter>
        )}
    </>
  );

  if (embedded) {
    // Match HumanInterviewHub's content gutter (px-6) and reserve space for
    // the sticky footer that lives at the bottom of the panel.
    return <div className="px-6 pb-0 pt-5">{panel}</div>;
  }

  return (
    <Dialog open={open} onOpenChange={(value) => !value && resetAndClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">{panel}</DialogContent>
    </Dialog>
  );
};

export default MeetingCreateDialog;
